import { useState, useCallback } from 'react'
import { useCreateDatabaseMutation } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { useFormValidation } from '@/hooks/useFormValidation'
import type { DatabaseConnection } from '@/stores/connectionStore'
import {
  ValidatedInput,
  ValidatedSelect,
  ValidationSummary,
  FormSection,
  FormActions
} from '@/components/common/ValidatedForm'

interface CreateDatabaseViewProps {
  connectionId: string
  databaseType: 'MySQL' | 'PostgreSQL'
  onDatabaseCreated?: () => void
  onCancel?: () => void
}

// MySQL character sets and their common collations
const mysqlCharsets = {
  'utf8mb4': [
    'utf8mb4_unicode_ci',
    'utf8mb4_general_ci',
    'utf8mb4_bin',
    'utf8mb4_0900_ai_ci'
  ],
  'utf8': [
    'utf8_unicode_ci',
    'utf8_general_ci',
    'utf8_bin'
  ],
  'latin1': [
    'latin1_swedish_ci',
    'latin1_general_ci',
    'latin1_bin'
  ],
  'ascii': [
    'ascii_general_ci',
    'ascii_bin'
  ]
}

// PostgreSQL encodings and templates
const postgresEncodings = [
  'UTF8',
  'SQL_ASCII',
  'LATIN1',
  'LATIN2',
  'LATIN3',
  'LATIN4',
  'LATIN5',
  'LATIN6',
  'LATIN7',
  'LATIN8',
  'LATIN9',
  'ISO_8859_5',
  'ISO_8859_6',
  'ISO_8859_7',
  'ISO_8859_8',
  'KOI8R',
  'KOI8U',
  'WIN1250',
  'WIN1251',
  'WIN1252',
  'WIN1253',
  'WIN1254',
  'WIN1255',
  'WIN1256',
  'WIN1257',
  'WIN1258'
]

const postgresTemplates = [
  'template0',
  'template1'
]

export function CreateDatabaseView({ connectionId, databaseType, onDatabaseCreated, onCancel }: CreateDatabaseViewProps) {
  const { showToast } = useToast()
  const createDatabaseMutation = useCreateDatabaseMutation()

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    charset: 'utf8mb4',
    collation: 'utf8mb4_unicode_ci',
    encoding: 'UTF8',
    template: 'template0'
  })

  // Form validation setup
  const { 
    validateField,
    validateForm,
    clearValidation,
    errors,
    warnings
  } = useFormValidation(
    {
      id: connectionId,
      name: 'Mock Connection',
      type: databaseType === 'MySQL' ? 'mysql' : 'postgresql',
      host: 'localhost',
      port: databaseType === 'MySQL' ? 3306 : 5432,
      username: 'user',
      password: 'password',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } as DatabaseConnection, // Mock connection for validation
    [] // No existing names needed for database creation
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const hasKey = <T extends object>(obj: T, key: PropertyKey): key is keyof T => key in obj

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      charset: 'utf8mb4',
      collation: 'utf8mb4_unicode_ci',
      encoding: 'UTF8',
      template: 'template0'
    })
    clearValidation()
    setIsSubmitting(false)
  }, [clearValidation])

  const handleFieldChange = useCallback(async (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    if (field === 'name') {
      await validateField(field, value, {
        required: true,
        type: 'identifier',
        maxLength: databaseType === 'MySQL' ? 64 : 63
      })
    }
  }, [validateField, databaseType])

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    
    const isFormValid = await validateForm(
      { name: formData.name },
      { name: { required: true, type: 'identifier', maxLength: databaseType === 'MySQL' ? 64 : 63 } }
    )
    
    if (!isFormValid.isValid) return

    setIsSubmitting(true)

    try {
      let requestBody: { name: string; charset?: string; collation?: string; encoding?: string; template?: string }

      requestBody = databaseType === 'MySQL' ? {
          name: formData.name,
          charset: formData.charset,
          collation: formData.collation
        } : {
          name: formData.name,
          encoding: formData.encoding,
          template: formData.template
        };

      await createDatabaseMutation.mutateAsync({
        datasourceId: connectionId,
        requestBody
      })

      showToast('success', 
        'Success',
        `Database "${formData.name}" created successfully`
      )

      resetForm()
      onDatabaseCreated?.()
    } catch (error) {
      console.error('Failed to create database:', error)

      let errorMessage = 'Failed to create database'
      if (error && typeof error === 'object' && 'body' in error &&
          error.body && typeof error.body === 'object' && 'message' in error.body) {
        errorMessage = String(error.body.message)
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      showToast('error', 'Error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }, [formData.name, validateForm, databaseType, createDatabaseMutation, connectionId, formData.charset, formData.collation, formData.encoding, formData.template, showToast, resetForm, onDatabaseCreated])

  const getAvailableCollations = (charset: string) => {
    if (hasKey(mysqlCharsets, charset)) {
      return mysqlCharsets[charset]
    }
    return []
  }

  const handleCharsetChange = useCallback((charset: string) => {
    const availableCollations = getAvailableCollations(charset)
    setFormData(prev => ({
      ...prev,
      charset,
      collation: availableCollations[0] || ''
    }))
  }, [])

  const handleCollationChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, collation: value }))
  }, [])

  const handleEncodingChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, encoding: value }))
  }, [])

  const handleTemplateChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, template: value }))
  }, [])

  return (
    <div className="h-full bg-white dark:bg-gray-900 p-6 overflow-y-auto">
      <div className="max-w-2xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Create {databaseType} Database
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {databaseType === 'MySQL' 
              ? 'Create a new MySQL database with custom character set and collation settings.'
              : 'Create a new PostgreSQL database with custom encoding and template settings.'
            }
          </p>
        </div>

        {/* Validation Summary */}
        <ValidationSummary errors={errors} warnings={warnings} />

        {/* Form */}
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          <FormSection title="Database Configuration">
            <ValidatedInput
              label="Database Name"
              name="name"
              value={formData.name}
              onChange={useCallback((value: string) => { void handleFieldChange('name', value) }, [handleFieldChange])}
              error={errors.name}
              placeholder="Enter database name"
              required
              disabled={isSubmitting}
              helpText="Database name must start with a letter or underscore and contain only alphanumeric characters and underscores."
            />

            {databaseType === 'MySQL' ? (
              <>
                <ValidatedSelect
                  label="Character Set"
                  name="charset"
                  value={formData.charset}
                  onChange={handleCharsetChange}
                  options={Object.keys(mysqlCharsets).map(charset => ({
                    value: charset,
                    label: charset.toUpperCase()
                  }))}
                  disabled={isSubmitting}
                  helpText="Character set determines which characters can be stored in the database."
                />

                <ValidatedSelect
                  label="Collation"
                  name="collation"
                  value={formData.collation}
                  onChange={handleCollationChange}
                  options={getAvailableCollations(formData.charset).map(collation => ({
                    value: collation,
                    label: collation
                  }))}
                  disabled={isSubmitting}
                  helpText="Collation determines how strings are compared and sorted."
                />
              </>
            ) : (
              <>
                <ValidatedSelect
                  label="Encoding"
                  name="encoding"
                  value={formData.encoding}
                  onChange={handleEncodingChange}
                  options={postgresEncodings.map(encoding => ({
                    value: encoding,
                    label: encoding
                  }))}
                  disabled={isSubmitting}
                  helpText="Character encoding for the database. UTF8 is recommended for most use cases."
                />

                <ValidatedSelect
                  label="Template Database"
                  name="template"
                  value={formData.template}
                  onChange={handleTemplateChange}
                  options={postgresTemplates.map(template => ({
                    value: template,
                    label: template
                  }))}
                  disabled={isSubmitting}
                  helpText="Template to use when creating the database. template0 is a clean template."
                />
              </>
            )}
          </FormSection>

          <FormActions
            onSubmit={useCallback(() => { void handleSubmit() }, [handleSubmit])}
            onCancel={onCancel || resetForm}
            submitLabel="Create Database"
            isValid={!!formData.name.trim() && Object.keys(errors).length === 0}
            isSubmitting={isSubmitting}
          />
        </form>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
            💡 Tips for {databaseType} Databases
          </h3>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            {databaseType === 'MySQL' ? (
              <>
                <li>• Use utf8mb4 charset for full Unicode support including emojis</li>
                <li>• utf8mb4_unicode_ci collation provides the best Unicode sorting</li>
                <li>• Database names are case-sensitive on Linux/macOS but not on Windows</li>
              </>
            ) : (
              <>
                <li>• UTF8 encoding supports all Unicode characters</li>
                <li>• template0 is a clean template without any optional features</li>
                <li>• template1 includes additional language and features</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}
